import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    initiateEasebuzzPaymentThunk,
    initiateEasebuzzPaymentThunkRecharge,
    handleEasebuzzResponse,
    clearError,
    resetPayment,
    selectPaymentState,
} from "../store/paymentSlices/paymentSlice";
import { openEasebuzzPaymentModal, openCashfreePaymentModal } from "../services/paymentService";

/**
 * Reusable payment hook for any booking flow (flights, holidays, wallet recharge).
 *
 * Pass `recharge: true` for top-up / recharge style payments (separate Redux
 * fields so a recharge payment doesn't clobber an in-progress booking payment
 * elsewhere in the app). Defaults to the primary booking flow.
 *
 * Automatically opens Cashfree when `ActivePG === 2` (as returned by your
 * backend's /initiate_payment response) and Easebuzz otherwise — same
 * branching your old usePaymentProcessRecharge did.
 *
 * usage:
 *   const { initiatePayment, loading, paymentStatus } = usePaymentGateway({
 *     onSuccess: (response) => { ... },
 *     onFailure: (response) => { ... },
 *   });
 *   initiatePayment(formData);
 *
 *   // recharge / wallet top-up variant:
 *   const { initiatePayment } = usePaymentGateway({ recharge: true, onSuccess, onFailure });
 */
export function usePaymentGateway({ recharge = false, onSuccess, onFailure } = {}) {
    const dispatch = useDispatch();
    const paymentState = useSelector(selectPaymentState);
    const pendingRef = useRef(false);
    const loading = recharge ? paymentState.loadingRecharge : paymentState.loading;
    const error = recharge ? paymentState.errorRecharge : paymentState.error;
    const paymentInitiated = recharge ? paymentState.paymentInitiatedRecharge : paymentState.paymentInitiated;
    const paymentData = recharge ? paymentState.paymentDataRecharge : paymentState.paymentData;
    const paymentStatus = recharge ? paymentState.paymentStatusRecharge : paymentState.paymentStatus;

    // keep latest callbacks without re-subscribing the effect every render
    const callbacksRef = useRef({ onSuccess, onFailure });
    callbacksRef.current = { onSuccess, onFailure };

    const initiatePayment = useCallback(
        (formData) => {
            if (!formData) {
                callbacksRef.current.onFailure?.({ status: "failed", message: "Missing payment details" });
                return;
            }
            pendingRef.current = true;  
            dispatch(clearError());
            dispatch(recharge ? initiateEasebuzzPaymentThunkRecharge(formData) : initiateEasebuzzPaymentThunk(formData));
        },
        [dispatch, recharge]
    );

    const reset = useCallback(() => dispatch(resetPayment()), [dispatch]);

    // Once the thunk resolves with an accessKey, open the right checkout modal.
    useEffect(() => {
        if (!pendingRef.current) return;
        if (!paymentInitiated || !paymentData?.accessKey) return;
        pendingRef.current = false;

        const onModalResponse = (response) => {
            dispatch(handleEasebuzzResponse(response));
            if (response.status === "success") {
                callbacksRef.current.onSuccess?.(response);
            } else {
                callbacksRef.current.onFailure?.(response);
            }
        };

        try {
            if (paymentData.ActivePG === 2 || paymentData.ActivePG === "2") {
                openCashfreePaymentModal(paymentData.enviroment, paymentData.accessKey, paymentData.paymentDetails, onModalResponse);
            } else {
                openEasebuzzPaymentModal(paymentData.enviroment, paymentData.accessKey, paymentData.paymentDetails, onModalResponse);
            }
        } catch {
            callbacksRef.current.onFailure?.({ status: "failed", message: "Failed to open payment modal" });
        }
        // only re-run when a *new* payment is initiated
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentInitiated, paymentData]);

    return { initiatePayment, reset, loading, paymentStatus, error };
}